import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarSingle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  parseCalendarDate,
} from '@resource-kit/ui/react';
import { cn } from '@resource-kit/ui';
import { PageToolbar, PageToolbarGroup } from '@components/ui/PageToolbar';
import type { BoardWindow } from '../board/boardModel';
import type { DeliveryFlowWire } from '../board/boardWire';
import { EXECUTOR_LABEL, flowProvider } from '../board/flowCopy';
import { formatBatchDay, formatMonthDay } from '../board/formatBatchDay';

// Literal keys: `t()` reads these by lookup, which the i18n source-coverage
// scan cannot follow.
const FLOW_BATCH_LABEL = {
  shipping_date: 'dispatch.header.flowBatch.shipping_date',
} as const satisfies Record<DeliveryFlowWire['batch'], string>;

const TEMPLATE_LABEL = {
  hgs_same_day: 'deliveryFlow.template.hgs_same_day',
} as const satisfies Record<DeliveryFlowWire['template'], string>;

export type DispatchView = 'board' | 'list' | 'map';

/**
 * "Flow: <template> · <batch> · <n> states · <provider> Settings". Every part
 * comes from the tenant flow; the settings link is for admins only, the same
 * test the sidebar uses for that entry.
 */
export function DispatchFlowChip({ flow, isAdmin }: { flow: DeliveryFlowWire; isAdmin: boolean }) {
  const { t } = useTranslation();
  const provider = flowProvider(flow);
  const meta = [
    t(FLOW_BATCH_LABEL[flow.batch]),
    t('dispatch.header.flowStates', { count: flow.columns.length }),
    provider ? t(EXECUTOR_LABEL[provider]) : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
  return (
    <div
      data-testid="dispatch-flow-chip"
      className="flex flex-wrap items-center gap-x-2 rounded-lg bg-muted px-2.5 py-1 text-[12.5px] text-muted-foreground"
    >
      <span>
        {t('dispatch.header.flowLabel')}
        <b className="text-foreground">{t(TEMPLATE_LABEL[flow.template])}</b>
      </span>
      <span aria-hidden="true">·</span>
      <span>{meta}</span>
      {isAdmin ? (
        <Link to="/settings/delivery-flow" className="text-primary hover:underline">
          {t('dispatch.header.flowSettings')}
        </Link>
      ) : null}
    </div>
  );
}

function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

interface DispatchHeaderActionsProps {
  view: DispatchView;
  onSelectView: (view: DispatchView) => void;
  /** The work date; the list and map views show this one day. */
  date: string;
  onPickDate: (date: string) => void;
  boardWin: BoardWindow;
  /** The list and map views sit behind `dispatch:read`. */
  canViewDispatch: boolean;
}

const SEGMENT_BUTTON =
  'inline-flex h-7 items-center rounded-md px-2.5 text-[12.5px] text-muted-foreground transition-colors hover:text-foreground aria-pressed:bg-background aria-pressed:font-medium aria-pressed:text-foreground aria-pressed:shadow-sm';

/** The view switch and the range label, which doubles as the date picker. */
export function DispatchHeaderActions({
  view,
  onSelectView,
  date,
  onPickDate,
  boardWin,
  canViewDispatch,
}: DispatchHeaderActionsProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const rangeLabel =
    view === 'board'
      ? t('dispatch.header.range', {
          from: formatMonthDay(boardWin.labelFrom, i18n.language),
          to: formatMonthDay(boardWin.labelTo, i18n.language),
        })
      : formatBatchDay(date, i18n.language);

  return (
    <PageToolbar>
      <PageToolbarGroup>
        <div
          role="group"
          aria-label={t('dispatch.view.label')}
          className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-0.5"
        >
          <button
            type="button"
            data-testid="dispatch-view-board"
            aria-pressed={view === 'board'}
            className={SEGMENT_BUTTON}
            onClick={() => onSelectView('board')}
          >
            {t('dispatch.view.board')}
          </button>
          {canViewDispatch ? (
            <button
              type="button"
              data-testid="dispatch-view-list"
              aria-pressed={view === 'list'}
              className={SEGMENT_BUTTON}
              onClick={() => onSelectView('list')}
            >
              {t('dispatch.view.list')}
            </button>
          ) : null}
          {canViewDispatch ? (
            <button
              type="button"
              data-testid="dispatch-view-map"
              aria-pressed={view === 'map'}
              className={SEGMENT_BUTTON}
              onClick={() => onSelectView('map')}
            >
              {t('dispatch.view.map')}
            </button>
          ) : null}
        </div>
      </PageToolbarGroup>
      <PageToolbarGroup>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <button
                type="button"
                data-testid="dispatch-range-picker"
                data-selected-date={date}
                title={t('dispatch.header.pickDate')}
                className={cn(
                  'rounded-md px-1.5 py-1 text-[12.5px] tabular-nums text-muted-foreground',
                  'hover:bg-muted hover:text-foreground',
                )}
              />
            }
          >
            {rangeLabel}
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2">
            <CalendarSingle
              selected={parseCalendarDate(date) ?? undefined}
              onSelect={(picked) => {
                if (!picked) return;
                onPickDate(toDateString(picked));
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </PageToolbarGroup>
    </PageToolbar>
  );
}
